/**
 * Synthesised sound. No audio files.
 *
 * Not a shortcut around missing assets — for this kind of game it is the better answer.
 * Simon works entirely on four tones, and a child repeating a sequence they can HEAR is
 * playing music rather than memorising positions. Synthesis also means no download on a
 * metered Iranian mobile connection, and nothing to translate or license.
 *
 * **The pentatonic trick.** Notes are drawn from a pentatonic scale, which has no semitone
 * clashes — so ANY order of tiles sounds like a tune rather than like a mistake. That single
 * choice is what lets a randomly generated sequence be pleasant, and it is why the scale is
 * not a major scale with more notes available.
 *
 * **What this does and does not affect.** A tone on a child's own tap is feedback and changes
 * nothing about the instrument. A tone during PRESENTATION makes the stimulus audiovisual
 * instead of visual — a different instrument, requiring a declared calibration bump. The span
 * tasks take that bump deliberately; see the span tasks in `task-defs.ts`.
 */

const MUTE_KEY = "kiddora.muted";

/**
 * A pentatonic run, low to high, in Hz.
 *
 * Nine notes for a 3×3 grid, so each tile has its own pitch and the grid reads bottom-left
 * to top-right as an ascending instrument a child can learn the shape of.
 */
const SCALE = [261.63, 293.66, 329.63, 392.0, 440.0, 523.25, 587.33, 659.25, 783.99];

let context: AudioContext | null = null;
let muted = read();

function read(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === "true";
  } catch {
    return false;
  }
}

export function isMuted(): boolean {
  return muted;
}

export function setMuted(next: boolean): void {
  muted = next;
  try {
    localStorage.setItem(MUTE_KEY, String(next));
  } catch {
    /* A preference that cannot persist still applies for this session. */
  }
}

/**
 * Creates or resumes the audio context.
 *
 * Must be called from a real user gesture: browsers refuse to start audio otherwise, and a
 * context created on page load arrives permanently suspended — which presents as "the game
 * is silent on this phone" with nothing in the console.
 */
export function unlockAudio(): void {
  if (muted) return;
  try {
    context ??= new AudioContext();
    if (context.state === "suspended") void context.resume();
  } catch {
    // No Web Audio on this engine. Every play call below becomes a no-op; the games are
    // fully playable without sound, so this is a degradation and not a failure.
    context = null;
  }
}

interface ToneOptions {
  durationMs?: number;
  /** Peak gain. Kept low — this is played near a child's face. */
  volume?: number;
  type?: OscillatorType;
  /** Seconds from now. Used to schedule a chord or an arpeggio precisely. */
  delay?: number;
}

function play(frequency: number, opts: ToneOptions = {}): void {
  if (muted || !context) return;
  const { durationMs = 260, volume = 0.16, type = "sine", delay = 0 } = opts;

  const start = context.currentTime + delay;
  const osc = context.createOscillator();
  const gain = context.createGain();

  osc.type = type;
  osc.frequency.value = frequency;

  // An envelope, not a switch. A gain that jumps to full produces the click that makes
  // synthesised sound feel cheap; 12 ms of attack and an exponential tail removes it.
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(volume, start + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + durationMs / 1000);

  osc.connect(gain).connect(context.destination);
  osc.start(start);
  osc.stop(start + durationMs / 1000 + 0.02);
}

/** The note belonging to a grid cell. Wraps, so a larger grid still sounds musical. */
export function cellTone(cellIndex: number, opts: ToneOptions = {}): void {
  const note = SCALE[cellIndex % SCALE.length];
  if (note !== undefined) play(note, { durationMs: 300, ...opts });
}

/** A rising arpeggio. The reward. */
export function successChime(): void {
  [0, 2, 4, 7].forEach((step, i) => {
    const note = SCALE[step % SCALE.length];
    if (note !== undefined) {
      play(note, { delay: i * 0.075, durationMs: 420, volume: 0.14, type: "triangle" });
    }
  });
}

/**
 * A soft, low, two-note fall for a wrong answer.
 *
 * Deliberately gentle and short. A harsh buzzer teaches a child to stop trying, and v2 §19
 * is explicit that frustration must not be recorded as inability — so the sound says "not
 * that one", never "you failed".
 */
export function missTone(): void {
  play(196.0, { durationMs: 200, volume: 0.1, type: "sine" });
  play(174.61, { delay: 0.1, durationMs: 260, volume: 0.09, type: "sine" });
}

/** A neutral tick for a step that is neither right nor wrong, like clearing a cell. */
export function tick(): void {
  play(880, { durationMs: 70, volume: 0.06, type: "triangle" });
}

/** Played as a level opens, so starting something feels like an event. */
export function levelUp(): void {
  [4, 5, 7].forEach((step, i) => {
    const note = SCALE[step % SCALE.length];
    if (note !== undefined) {
      play(note, { delay: i * 0.06, durationMs: 300, volume: 0.12, type: "triangle" });
    }
  });
}
